import React, {useCallback, useState} from 'react';
import {addToast, Button, Dialogue, Modal, Select, TextArea} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import {UserFeedbackReportedDevice, UserFeedbackType} from 'gql/types';

import {useCreateUserFeedbackMutation} from './IssuesModal.queries.generated';

interface IssuesModalProps {
  questionId: string;
  handleClose: () => void;
}

const TYPE_OPTIONS = [
  {value: UserFeedbackType.Typo, text: 'Typo'},
  {value: UserFeedbackType.Inaccuracy, text: 'Inaccuracy'},
  {value: UserFeedbackType.Image, text: 'Image'},
  {value: UserFeedbackType.Formatting, text: 'Formatting'},
  {value: UserFeedbackType.Suggestion, text: 'Suggestion'},
  {value: UserFeedbackType.TranslationError, text: 'Translation Error'}
] as const;

const REPORTED_DEVICE_OPTIONS = [
  {value: UserFeedbackReportedDevice.Desktop, text: 'Desktop'},
  {value: UserFeedbackReportedDevice.Mobile, text: 'Mobile'}
] as const;

export const IssuesModal = ({questionId, handleClose}: IssuesModalProps) => {
  const [saving, setSaving] = useState(false);
  const [type, setType] = useState<UserFeedbackType | ''>('');
  const [reportedDevice, setReportedDevice] = useState<UserFeedbackReportedDevice | ''>('');
  const [comment, setComment] = useState('');
  const [createUserFeedback] = useCreateUserFeedbackMutation();

  const submit = useCallback(async () => {
    if (!type || !reportedDevice) {
      return;
    }
    setSaving(true);
    try {
      const {data} = await createUserFeedback({
        variables: {
          input: {
            comment,
            feedbackType: type,
            reportedDevice,
            questionId,
            userId: sessionStore.getUserId()
          }
        }
      });
      if (data?.createUserFeedback?.errors?.length) {
        addToast({
          message: 'There was a problem processing your question feedback, please try again.',
          title: 'Error',
          color: 'negative'
        });
      } else {
        addToast({
          message: 'Your question feedback has been received.',
          title: 'Success',
          color: 'positive'
        });
      }
    } catch (_e) {
      addToast({
        message: 'There was a problem processing your question feedback, please try again.',
        title: 'Error',
        color: 'negative'
      });
    } finally {
      setSaving(false);
      handleClose();
    }
  }, [type, reportedDevice, comment, questionId, handleClose, createUserFeedback]);

  const canSubmit = type && reportedDevice && comment;

  return (
    <Modal ariaLabel='Submit Feedback' handleClose={handleClose}>
      {({CloseButtonWrapper, modalContentStyle}) => (
        <Dialogue
          inModal
          size='l'
          className={modalContentStyle}
          handleClose={handleClose}
          title='Submit Question Feedback to Albert.io'
        >
          <Dialogue.Body>
            <div className='flex flex-col gap-x1'>
              <div className='flex items-center justify-between gap-x1'>
                <Select
                  aria-label='Issue type'
                  value={type}
                  onChange={(e) => setType(e.target.value as UserFeedbackType)}
                >
                  <option value='' disabled>
                    Select Issue Type
                  </option>
                  {TYPE_OPTIONS.map(({value, text}) => (
                    <option key={value} value={value}>
                      {text}
                    </option>
                  ))}
                </Select>

                <Select
                  aria-label='Reported device'
                  value={reportedDevice}
                  onChange={(e) => setReportedDevice(e.target.value as UserFeedbackReportedDevice)}
                >
                  <option value='' disabled>
                    Select Reported Device
                  </option>
                  {REPORTED_DEVICE_OPTIONS.map(({value, text}) => (
                    <option key={value} value={value}>
                      {text}
                    </option>
                  ))}
                </Select>
              </div>

              <TextArea
                aria-label='Feedback description'
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                style={{resize: 'vertical'}}
                placeholder='Please provide a detailed description of the issue you have with the question. If this is a technical issue, please email hello@albert.io.'
                rows={3}
              />
            </div>
          </Dialogue.Body>
          <Dialogue.BtnGroup>
            <Button disabled={!canSubmit || saving} color='primary' onClick={submit}>
              Submit
            </Button>
            <CloseButtonWrapper>
              <Button color='secondary' disabled={saving}>
                Close
              </Button>
            </CloseButtonWrapper>
          </Dialogue.BtnGroup>
        </Dialogue>
      )}
    </Modal>
  );
};
